// src/api.ts
import { z } from "zod";
var KstDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Expected YYYY-MM-DD (KST)");
var RectSchema = z.object({
  c1: z.number().int().min(0).max(16),
  r1: z.number().int().min(0).max(9),
  c2: z.number().int().min(0).max(16),
  r2: z.number().int().min(0).max(9)
});
var MoveSchema = z.object({
  rect: RectSchema,
  tsMs: z.number().int().nonnegative().max(125e3)
  // 120s game + 5s grace
});
var TelegramUserSchema = z.object({
  id: z.number().int().positive(),
  first_name: z.string().min(1).max(64),
  last_name: z.string().optional(),
  username: z.string().optional(),
  language_code: z.string().length(2).optional(),
  photo_url: z.string().url().optional()
});
var AuthInitRequestSchema = z.object({
  initData: z.string().min(1)
});
var AuthInitResponseSchema = z.object({
  ok: z.literal(true),
  user: z.object({
    telegramId: z.number(),
    firstName: z.string(),
    anonymousMode: z.boolean(),
    languageCode: z.string().nullable()
  })
});
var StaminaStateSchema = z.object({
  current: z.number().int().min(0).max(20),
  lastRegenAt: z.string().datetime(),
  nextRegenAt: z.string().datetime().nullable(),
  dailyBonusDate: KstDateSchema.nullable()
});
var ThemePrefSchema = z.enum(["light", "dark", "auto"]);
var LangPrefSchema = z.enum(["ko", "en"]);
var MeResponseSchema = z.object({
  user: z.object({
    telegramId: z.number(),
    firstName: z.string(),
    displayName: z.string(),
    anonymousMode: z.boolean(),
    languageCode: z.string().nullable(),
    /** W3W4 P2-b: cross-device-synced display preference. NULL = unset. */
    theme: ThemePrefSchema.nullable().optional(),
    /** W3W4 P2-b: cross-device-synced language. NULL = unset (falls back to client detection). */
    language: LangPrefSchema.nullable().optional(),
    /** W3W4 P2-b: cross-device-synced haptic toggle. NULL = unset (defaults ON). */
    hapticEnabled: z.boolean().nullable().optional()
  }),
  stamina: StaminaStateSchema,
  todayBest: z.number().int().nonnegative().nullable(),
  serverTime: z.string().datetime()
});
var MePatchRequestSchema = z.object({
  anonymousMode: z.boolean().optional(),
  theme: ThemePrefSchema.optional(),
  language: LangPrefSchema.optional(),
  hapticEnabled: z.boolean().optional()
});
var DailyHistoryQuerySchema = z.object({
  offset: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(50).default(20)
}).strict();
var DailyHistoryItemSchema = z.object({
  date: KstDateSchema,
  score: z.number().int().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  submittedAt: z.string().datetime(),
  rank: z.number().int().positive().nullable()
});
var DailyHistoryResponseSchema = z.object({
  ok: z.literal(true),
  items: z.array(DailyHistoryItemSchema),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().positive()
});
var DailyTodayResponseSchema = z.object({
  date: KstDateSchema,
  seed: z.number().int(),
  stamina: StaminaStateSchema,
  yourBest: z.number().int().nonnegative().nullable(),
  topScore: z.number().int().nonnegative().nullable(),
  attemptCount: z.number().int().nonnegative()
});
var DailyStartResponseSchema = z.object({
  ok: z.literal(true),
  startedAtMs: z.number().int().nonnegative(),
  nonce: z.string(),
  // server-signed, single-use for /submit
  seed: z.number().int(),
  staminaAfter: z.number().int().min(0).max(20)
});
var DailyStartErrorSchema = z.object({
  ok: z.literal(false),
  reason: z.enum(["NO_STAMINA", "GAME_IN_PROGRESS", "INVALID_DATE"]),
  stamina: StaminaStateSchema.optional()
});
var DailySubmitRequestSchema = z.object({
  date: KstDateSchema,
  startedAtMs: z.number().int().nonnegative(),
  nonce: z.string(),
  clientFinalScore: z.number().int().nonnegative(),
  moves: z.array(MoveSchema).max(1e3)
  // sanity bound
});
var DailySubmitResponseSchema = z.object({
  ok: z.literal(true),
  serverScore: z.number().int().nonnegative(),
  newDailyBest: z.boolean(),
  newWeeklyBest: z.boolean(),
  rank: z.number().int().positive().nullable()
});
var DailySubmitErrorSchema = z.object({
  ok: z.literal(false),
  reason: z.enum([
    "INVALID_NONCE",
    "NONCE_USED",
    "SCORE_MISMATCH",
    "INVALID_MOVE_LOG",
    "NO_ACTIVE_ATTEMPT"
  ]),
  divergenceIndex: z.number().int().nullable().optional()
});
var LeaderboardEntrySchema = z.object({
  rank: z.number().int().positive(),
  userId: z.number(),
  displayName: z.string(),
  score: z.number().int().nonnegative(),
  isYou: z.boolean().optional()
});
var DailyLeaderboardResponseSchema = z.object({
  date: KstDateSchema,
  totalPlayers: z.number().int().nonnegative(),
  entries: z.array(LeaderboardEntrySchema),
  yourRank: z.number().int().positive().nullable()
});
var WeeklyLeaderboardResponseSchema = z.object({
  weekStartKst: KstDateSchema,
  totalPlayers: z.number().int().nonnegative(),
  entries: z.array(LeaderboardEntrySchema),
  yourRank: z.number().int().positive().nullable()
});
var HallOfFameEntrySchema = z.object({
  weekStartKst: KstDateSchema,
  rank: z.number().int().min(1).max(10),
  userId: z.number(),
  displayName: z.string(),
  bestScore: z.number().int().nonnegative(),
  archivedAt: z.string().datetime()
});
var HallOfFameResponseSchema = z.object({
  entries: z.array(HallOfFameEntrySchema)
});
var SharePrepareRequestSchema = z.object({
  date: KstDateSchema,
  score: z.number().int().nonnegative(),
  lang: z.enum(["ko", "en"]).optional().default("ko")
});
var SharePrepareResponseSchema = z.object({
  ok: z.literal(true),
  shareToken: z.string(),
  preparedInlineMessageId: z.string().optional(),
  imageUrl: z.string().url(),
  deepLink: z.string().url()
});
var SharePrepareErrorSchema = z.object({
  ok: z.literal(false),
  reason: z.enum(["NO_ATTEMPT", "BOT_API_ERROR", "IMAGE_GEN_FAILED"])
});
var AdminOverviewResponseSchema = z.object({
  dau: z.number().int().nonnegative(),
  wau: z.number().int().nonnegative(),
  mau: z.number().int().nonnegative(),
  todayAttempts: z.number().int().nonnegative(),
  todayCompletions: z.number().int().nonnegative(),
  completionRate: z.number().min(0).max(1),
  thisWeekAttempts: z.number().int().nonnegative(),
  thisWeekValidated: z.number().int().nonnegative(),
  antiCheatRecent: z.number().int().nonnegative(),
  shareCtr: z.number().min(0).max(1).nullable()
});
var AdminAntiCheatAttemptDetailSchema = z.object({
  score: z.number().int(),
  clientScore: z.number().int(),
  serverScore: z.number().int(),
  divergenceIndex: z.number().int().nullable(),
  moveCount: z.number().int().nonnegative(),
  submittedAt: z.string().datetime()
});
var AdminAntiCheatEntrySchema = z.object({
  id: z.number(),
  userId: z.number(),
  reason: z.string(),
  eventDate: KstDateSchema,
  attemptId: z.number().nullable(),
  count: z.number().int().nonnegative(),
  details: z.unknown().nullable(),
  firstSeenAt: z.string().datetime(),
  lastSeenAt: z.string().datetime(),
  attempt: AdminAntiCheatAttemptDetailSchema.nullable()
});
var AdminAntiCheatResponseSchema = z.object({
  events: z.array(AdminAntiCheatEntrySchema),
  total: z.number().int().nonnegative()
});
var AdminMatchDailyCountSchema = z.object({
  date: KstDateSchema,
  matchCount: z.number().int().nonnegative(),
  playerCount: z.number().int().nonnegative()
});
var AdminMatchRankCountSchema = z.object({
  rank: z.number().int().positive(),
  count: z.number().int().nonnegative()
});
var AdminMatchMetricsResponseSchema = z.object({
  totalMatches: z.number().int().nonnegative(),
  totalPlayers: z.number().int().nonnegative(),
  avgPlayersPerMatch: z.number().nonnegative(),
  avgDurationSec: z.number().nonnegative(),
  dailyMatches: z.array(AdminMatchDailyCountSchema),
  rankDistribution: z.array(AdminMatchRankCountSchema)
});
var AdminAuthRequestLinkResponseSchema = z.object({
  ok: z.literal(true)
});
var AdminAuthExchangeRequestSchema = z.object({
  token: z.string().min(1).max(128)
});
var AdminAuthExchangeResponseSchema = z.object({
  ok: z.literal(true),
  sessionToken: z.string(),
  expiresAtMs: z.number().int().positive()
});

export {
  KstDateSchema,
  RectSchema,
  MoveSchema,
  TelegramUserSchema,
  AuthInitRequestSchema,
  AuthInitResponseSchema,
  StaminaStateSchema,
  ThemePrefSchema,
  LangPrefSchema,
  MeResponseSchema,
  MePatchRequestSchema,
  DailyHistoryQuerySchema,
  DailyHistoryItemSchema,
  DailyHistoryResponseSchema,
  DailyTodayResponseSchema,
  DailyStartResponseSchema,
  DailyStartErrorSchema,
  DailySubmitRequestSchema,
  DailySubmitResponseSchema,
  DailySubmitErrorSchema,
  LeaderboardEntrySchema,
  DailyLeaderboardResponseSchema,
  WeeklyLeaderboardResponseSchema,
  HallOfFameEntrySchema,
  HallOfFameResponseSchema,
  SharePrepareRequestSchema,
  SharePrepareResponseSchema,
  SharePrepareErrorSchema,
  AdminOverviewResponseSchema,
  AdminAntiCheatAttemptDetailSchema,
  AdminAntiCheatEntrySchema,
  AdminAntiCheatResponseSchema,
  AdminMatchDailyCountSchema,
  AdminMatchRankCountSchema,
  AdminMatchMetricsResponseSchema,
  AdminAuthRequestLinkResponseSchema,
  AdminAuthExchangeRequestSchema,
  AdminAuthExchangeResponseSchema
};
